import { apiClient, getApiBase } from '@api/client';

const XLINK_NS = 'http://www.w3.org/1999/xlink';

function resolveToAbsoluteUrl(raw: string): string {
  const t = raw.trim();
  if (!t || t.startsWith('data:')) return t;
  try {
    return new URL(t, window.location.href).href;
  } catch {
    return t;
  }
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result as string);
    fr.onerror = () => reject(fr.error ?? new Error('FileReader failed'));
    fr.readAsDataURL(blob);
  });
}

function urlPathUnderApiBase(absUrl: string): string | null {
  const base = getApiBase().replace(/\/+$/, '');
  if (!absUrl.startsWith(base)) return null;
  const path = absUrl.slice(base.length);
  return path.startsWith('/') ? path : `/${path}`;
}

async function fetchImageAsDataUrl(url: string): Promise<string> {
  const apiPath = urlPathUnderApiBase(url);
  if (apiPath) {
    const { data } = await apiClient.get<Blob>(apiPath, { responseType: 'blob' });
    return blobToDataUrl(data);
  }

  const headers = new Headers();
  const token = localStorage.getItem('cuxa_token');
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const res = await fetch(url, { headers, credentials: 'include' });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return blobToDataUrl(await res.blob());
}

/**
 * Remplace les href des <image> par des data URLs, pour qu’un SVG sérialisé puis
 * chargé en blob: affiche les bitmaps (sinon sous-requêtes sans auth / mauvaise base URL).
 */
export async function inlineSvgRasterImages(svg: SVGSVGElement): Promise<void> {
  const nodes = svg.querySelectorAll('image');
  const cache = new Map<string, string>();

  for (const node of nodes) {
    const el = node as SVGImageElement;
    const raw =
      el.getAttribute('href') ?? el.getAttributeNS(XLINK_NS, 'href') ?? '';
    if (!raw || raw.startsWith('data:')) continue;

    const abs = resolveToAbsoluteUrl(raw);
    let dataUrl = cache.get(abs);
    if (!dataUrl) {
      try {
        dataUrl = await fetchImageAsDataUrl(abs);
      } catch (e) {
        console.warn('[export] impossible d’intégrer l’image', abs, e);
        continue;
      }
      cache.set(abs, dataUrl);
    }
    el.setAttribute('href', dataUrl);
    el.setAttributeNS(XLINK_NS, 'href', dataUrl);
  }
}
